import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * S4-T16 — Public read-only mode.
 *
 * Reads `?ro=1` from the current URL. When set, the app should hide all
 * interactive affordances (FABs, "Add" buttons, drag handlers, edit
 * actions) and surface a banner so the viewer knows they're in a shared
 * read-only context.
 *
 * Pattern: consumers call `useReadOnly()` directly. We intentionally do
 * not propagate via Context — every page already uses react-router hooks,
 * so a thin location-driven hook avoids the provider wrapping overhead.
 *
 * Recognised values: `?ro=1`, `?ro=true`, `?ro=yes` (case-insensitive).
 */
export function useReadOnly(): boolean {
  const location = useLocation();
  const [readOnly, setReadOnly] = useState(() => parseReadOnly(location.search));

  useEffect(() => {
    setReadOnly(parseReadOnly(location.search));
  }, [location.search]);

  return readOnly;
}

function parseReadOnly(search: string): boolean {
  try {
    const params = new URLSearchParams(search);
    const raw = params.get('ro');
    if (!raw) return false;
    const v = raw.toLowerCase();
    return v === '1' || v === 'true' || v === 'yes';
  } catch {
    return false;
  }
}
